import {
  Component,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  OnInit,
  inject,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import {
  TableComponent,
  TableColumn,
  withCellTemplates,
} from '../../../shared/components/primeng/table/table.component';
import {
  BenchmarkMySubmissionEntry,
  extractBenchmarkMySubmissionsList,
  CatalogueApiService,
  LeadershipApiService,
} from '@angular-workspace/services';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { ToasterService } from '../../../services/toaster.service';

export type SubmissionStatus = 'succeeded' | 'failed' | 'on_leaderboard';
export type SubmissionAction = 'use_for_leaderboard' | 'view_logs' | 'selected';

export interface MySubmissionRow {
  id: number;
  jobId: string;
  status: SubmissionStatus;
  samples: number;
  timeSeconds: number;
  metrics?: Record<string, number>;
  errorCode?: number;
  errorTitle?: string;
  errorMessage?: string;
  submitted: string;
  action: SubmissionAction;
}

export interface LeaderboardMetricComparisonRow {
  key: string;
  label: string;
  current: number | null;
  next: number;
  changeLabel: string;
}

interface DatasetTab {
  id: string;
  label: string;
}

/** Page size for the catalogue dataset list backing the dataset tabs. */
const DATASET_TAB_PAGE_SIZE = 100;

const ERROR_CODE_LABELS: Record<number, string> = {
  1: 'Dataloader / preprocessing error',
  2: 'Model loading error',
  3: 'Container error',
};

const PREFERRED_METRIC_ORDER = [
  'accuracy',
  'macro_f2',
  'weighted_f2',
  'macro_f1',
  'sensitivity',
  'qwk',
  'specificity',
  'npv',
  'ppv',
  'auc',
  'f2',
  'f1',
  'fnr',
  'fpr',
];

@Component({
  selector: 'app-my-submissions',
  standalone: true,
  imports: [CommonModule, TableComponent, ModalComponent],
  templateUrl: './my-submissions.component.html',
  styleUrl: './my-submissions.component.scss',
})
export class MySubmissionsComponent implements OnInit, AfterViewInit {
  @Input() showPanelHeader = true;

  private readonly leadershipApiService = inject(LeadershipApiService);
  private readonly catalogueApiService = inject(CatalogueApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toasterService = inject(ToasterService);
  private readonly route = inject(ActivatedRoute);

  /** Built from the catalogue; ids are the `dataset_id` UUIDs the leaderboard API expects. */
  datasetTabs: DatasetTab[] = [];

  activeDatasetId = '';
  submissions: MySubmissionRow[] = [];
  loading = true;
  private loadInFlight = false;
  loadError = '';

  leaderboardConfirmVisible = false;
  selectingLeaderboard = false;
  pendingSubmission: MySubmissionRow | null = null;
  currentLeaderboardSubmission: MySubmissionRow | null = null;

  viewLogsVisible = false;
  logSubmission: MySubmissionRow | null = null;

  metricsModalVisible = false;
  metricsModalSubmission: MySubmissionRow | null = null;

  tableColumns: TableColumn[] = [
    { key: 'id', label: 'ID', align: 'center', width: '64px' },
    { key: 'jobId', label: 'Job ID', width: '140px' },
    { key: 'status', label: 'Status', width: '130px' },
    { key: 'samples', label: 'Samples', align: 'right', width: '90px' },
    { key: 'timeSeconds', label: 'Time (s)', align: 'right', width: '90px' },
    { key: 'metricsOrError', label: 'Metrics', width: '220px' },
    { key: 'submitted', label: 'Submitted', width: '160px' },
    {
      key: 'action',
      label: 'Action',
      align: 'center',
      width: '160px',
      fixed: 'right',
    },
  ];

  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<unknown>;
  @ViewChild('idCell', { static: false }) idCellTemplate!: TemplateRef<unknown>;
  @ViewChild('jobIdCell', { static: false }) jobIdCellTemplate!: TemplateRef<unknown>;
  @ViewChild('metricsCell', { static: false }) metricsCellTemplate!: TemplateRef<unknown>;
  @ViewChild('timeCell', { static: false }) timeCellTemplate!: TemplateRef<unknown>;
  @ViewChild('samplesCell', { static: false }) samplesCellTemplate!: TemplateRef<unknown>;
  @ViewChild('submittedCell', { static: false }) submittedCellTemplate!: TemplateRef<unknown>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<unknown>;

  async ngOnInit(): Promise<void> {
    await this.loadDatasetTabs();
    if (!this.activeDatasetId) {
      this.loading = false;
      this.loadError = this.loadError || 'No datasets available.';
      return;
    }
    const requestedDataset = this.route.snapshot.queryParamMap.get('dataset')?.trim();
    if (requestedDataset) {
      const match = this.datasetTabs.find(
        (tab) => tab.id === requestedDataset || tab.label === requestedDataset,
      );
      if (match) {
        this.activeDatasetId = match.id;
      }
    }
    await this.loadSubmissions();
  }

  private async loadDatasetTabs(): Promise<void> {
    try {
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Dataset'],
        [],
        1,
        DATASET_TAB_PAGE_SIZE,
        'name:asc;itemCreatedAt:asc;',
      );
      this.datasetTabs = this.extractCatalogueItems(response)
        .filter((item) => item?.id)
        .map((item) => ({
          id: String(item.id),
          label: String(item.label || item.name || item.id),
        }));
    } catch (error) {
      console.error('Error loading datasets for my submissions:', error);
      this.datasetTabs = [];
      this.loadError = 'Unable to load datasets. Please try again.';
    }
    this.activeDatasetId = this.datasetTabs[0]?.id ?? '';
  }

  private extractCatalogueItems(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    const candidates = [
      response?.result,
      response?.result?.results,
      response?.results,
      response?.data,
      response?.items,
    ];
    return candidates.find((c) => Array.isArray(c)) ?? [];
  }

  ngAfterViewInit(): void {
    this.applyTableCellTemplates();
  }

  private applyTableCellTemplates(): void {
    if (
      !this.idCellTemplate ||
      !this.jobIdCellTemplate ||
      !this.statusCellTemplate ||
      !this.samplesCellTemplate ||
      !this.timeCellTemplate ||
      !this.metricsCellTemplate ||
      !this.submittedCellTemplate ||
      !this.actionCellTemplate
    ) {
      return;
    }

    this.tableColumns = withCellTemplates(this.tableColumns, [
      { key: 'id', cellTemplate: this.idCellTemplate },
      { key: 'jobId', cellTemplate: this.jobIdCellTemplate },
      { key: 'status', cellTemplate: this.statusCellTemplate },
      { key: 'samples', cellTemplate: this.samplesCellTemplate },
      { key: 'timeSeconds', cellTemplate: this.timeCellTemplate },
      { key: 'metricsOrError', cellTemplate: this.metricsCellTemplate },
      { key: 'submitted', cellTemplate: this.submittedCellTemplate },
      { key: 'action', cellTemplate: this.actionCellTemplate },
    ]);
    this.cdr.detectChanges();
  }

  async onDatasetChange(datasetId: string): Promise<void> {
    if (this.activeDatasetId === datasetId || this.loading) {
      return;
    }
    this.activeDatasetId = datasetId;
    await this.loadSubmissions();
  }

  async loadSubmissions(): Promise<void> {
    if (this.loadInFlight) {
      return;
    }

    this.loadInFlight = true;
    this.loading = true;
    this.loadError = '';

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.submissions = [];
        this.loadError = 'Sign in to view your submissions.';
        return;
      }

      const accessToken = await this.keycloak.getToken();
      const response = await this.leadershipApiService.getBenchmarkMySubmissions(
        this.activeDatasetId,
        accessToken,
      );
      const entries = extractBenchmarkMySubmissionsList(response);
      this.submissions = entries.map((entry) => this.mapApiEntry(entry));
      this.applyTableCellTemplates();
    } catch (error) {
      console.error('Error loading my submissions:', error);
      this.submissions = [];
      this.loadError = this.getErrorMessage(error);
    } finally {
      this.loadInFlight = false;
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private mapApiEntry(entry: BenchmarkMySubmissionEntry): MySubmissionRow {
    const apiStatus = (entry.status ?? '').toLowerCase();
    const isSelected =
      !!entry.is_selected ||
      entry['isSelected'] === true ||
      entry['on_leaderboard'] === true;

    let status: SubmissionStatus;
    let action: SubmissionAction;

    if (apiStatus === 'failed' || apiStatus === 'error') {
      status = 'failed';
      action = 'view_logs';
    } else if (isSelected) {
      status = 'on_leaderboard';
      action = 'selected';
    } else {
      status = 'succeeded';
      action = 'use_for_leaderboard';
    }

    const metrics = this.normalizeSubmissionMetrics(entry.metrics);
    // `error_code` / `error_type` are not declared on BenchmarkMySubmissionEntry, so they are read
    // through its index signature.
    const errorCode = entry['error_code'] as number | undefined;

    return {
      id: Number(entry.id),
      jobId: entry.job_id ?? '',
      status,
      samples: entry.num_samples ?? this.coerceSubmissionNumber(entry['numSamples']),
      timeSeconds: entry.elapsed_seconds ?? this.coerceSubmissionNumber(entry['elapsedSeconds']),
      metrics: apiStatus === 'failed' || apiStatus === 'error' ? undefined : metrics,
      errorCode:
        apiStatus === 'failed' || apiStatus === 'error' ? errorCode : undefined,
      errorTitle:
        apiStatus === 'failed' || apiStatus === 'error'
          ? this.coerceSubmissionString(entry['error_type'] ?? entry.error_title) ??
            ERROR_CODE_LABELS[errorCode ?? 0] ??
            'Evaluation failed'
          : undefined,
      errorMessage:
        apiStatus === 'failed' || apiStatus === 'error'
          ? this.coerceSubmissionString(entry.error_message ?? entry['errorMessage'])
          : undefined,
      submitted: this.formatSubmittedDate(this.getSubmissionSubmittedAt(entry)),
      action,
    };
  }

  private coerceSubmissionNumber(value: unknown, fallback = 0): number {
    const numeric = Number(value);
    return !Number.isNaN(numeric) && Number.isFinite(numeric) ? numeric : fallback;
  }

  private coerceSubmissionString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  private getSubmissionSubmittedAt(entry: BenchmarkMySubmissionEntry): string | undefined {
    const candidates = [
      entry.submitted_at,
      entry['submitted'],
      entry['created_at'],
      entry['submittedAt'],
    ];
    return candidates
      .map((value) => this.coerceSubmissionString(value))
      .find((value): value is string => !!value);
  }

  private normalizeSubmissionMetrics(
    metrics: unknown,
  ): Record<string, number> | undefined {
    if (metrics == null) {
      return undefined;
    }

    let source: unknown = metrics;
    if (typeof source === 'string') {
      try {
        source = JSON.parse(source);
      } catch {
        return undefined;
      }
    }

    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const normalized: Record<string, number> = {};
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
        normalized[key] = numericValue;
      }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private formatSubmittedDate(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return iso;
    }
    return new Date(parsed).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  private getErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'detail' in error) {
      const detail = (error as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail.trim();
      }
    }
    if (error instanceof Error && error.message.trim()) {
      return error.message.trim();
    }
    return 'Unable to load submissions. Please try again.';
  }

  truncateJobId(jobId: string): string {
    if (jobId.length <= 12) {
      return jobId;
    }
    return `${jobId.slice(0, 8)}...`;
  }

  formatTime(seconds: number): string {
    return seconds.toFixed(2);
  }

  formatMetricKey(key: string): string {
    return key.replace(/_/g, ' ');
  }

  formatMetricValue(value: number): string {
    return value.toFixed(4);
  }

  getMetricEntries(metrics: Record<string, number> | undefined): { key: string; value: number }[] {
    return this.getOrderedMetricEntries(metrics);
  }

  getPreviewMetricEntries(metrics: Record<string, number> | undefined): { key: string; value: number }[] {
    return this.getOrderedMetricEntries(metrics).slice(0, 2);
  }

  getOrderedMetricEntries(metrics: Record<string, number> | undefined): { key: string; value: number }[] {
    if (!metrics) {
      return [];
    }
    return this.orderMetricKeys(Object.keys(metrics)).map((key) => ({
      key,
      value: metrics[key],
    }));
  }

  hasMetrics(metrics: Record<string, number> | undefined): boolean {
    return this.getOrderedMetricEntries(metrics).length > 0;
  }

  hasMoreMetrics(metrics: Record<string, number> | undefined): boolean {
    return this.getOrderedMetricEntries(metrics).length > 2;
  }

  openMetricsModal(row: MySubmissionRow): void {
    this.metricsModalSubmission = row;
    this.metricsModalVisible = true;
    this.cdr.markForCheck();
  }

  closeMetricsModal(): void {
    this.metricsModalVisible = false;
    this.metricsModalSubmission = null;
    this.cdr.markForCheck();
  }

  getStatusLabel(status: SubmissionStatus): string {
    const labels: Record<SubmissionStatus, string> = {
      succeeded: 'succeeded',
      failed: 'failed',
      on_leaderboard: 'On leaderboard',
    };
    return labels[status];
  }

  getStatusClass(status: SubmissionStatus): string {
    const classes: Record<SubmissionStatus, string> = {
      succeeded: 'my-submissions-status--succeeded',
      failed: 'my-submissions-status--failed',
      on_leaderboard: 'my-submissions-status--on-leaderboard',
    };
    return classes[status];
  }

  getActionLabel(action: SubmissionAction): string {
    const labels: Record<SubmissionAction, string> = {
      use_for_leaderboard: 'Use for leaderboard',
      view_logs: 'View Logs',
      selected: 'Selected',
    };
    return labels[action];
  }

  getActionClass(action: SubmissionAction): string {
    const classes: Record<SubmissionAction, string> = {
      use_for_leaderboard: 'my-submissions-action--primary',
      view_logs: 'my-submissions-action--link',
      selected: 'my-submissions-action--selected',
    };
    return classes[action];
  }

  onActionClick(row: MySubmissionRow): void {
    if (row.action === 'use_for_leaderboard') {
      this.openLeaderboardConfirm(row);
      return;
    }
    if (row.action === 'view_logs') {
      this.openViewLogs(row);
    }
  }

  openViewLogs(row: MySubmissionRow): void {
    this.logSubmission = row;
    this.viewLogsVisible = true;
    this.cdr.markForCheck();
  }

  closeViewLogs(): void {
    this.viewLogsVisible = false;
    this.logSubmission = null;
    this.cdr.markForCheck();
  }

  getLogDisplayText(row: MySubmissionRow): string {
    const title = row.errorTitle?.trim();
    const message = row.errorMessage?.trim();

    if (title && message) {
      return `${title}\n\n${message}`;
    }
    if (message) {
      return message;
    }
    if (title) {
      return title;
    }
    return 'No error details available.';
  }

  openLeaderboardConfirm(row: MySubmissionRow): void {
    this.pendingSubmission = row;
    this.currentLeaderboardSubmission =
      this.submissions.find((submission) => submission.status === 'on_leaderboard') ?? null;
    this.leaderboardConfirmVisible = true;
    this.cdr.markForCheck();
  }

  closeLeaderboardConfirm(): void {
    if (this.selectingLeaderboard) {
      return;
    }
    this.leaderboardConfirmVisible = false;
    this.pendingSubmission = null;
    this.currentLeaderboardSubmission = null;
    this.cdr.markForCheck();
  }

  get leaderboardMetricRows(): LeaderboardMetricComparisonRow[] {
    if (!this.pendingSubmission?.metrics) {
      return [];
    }

    const nextMetrics = this.pendingSubmission.metrics;
    const currentMetrics = this.currentLeaderboardSubmission?.metrics;
    const keys = this.orderMetricKeys([
      ...Object.keys(nextMetrics),
      ...(currentMetrics ? Object.keys(currentMetrics) : []),
    ]);

    return keys.map((key) => {
      const current =
        currentMetrics && typeof currentMetrics[key] === 'number'
          ? currentMetrics[key]
          : null;
      const next = nextMetrics[key];
      return {
        key,
        label: key,
        current,
        next,
        changeLabel: this.formatMetricChange(current, next),
      };
    });
  }

  async confirmAddToLeaderboard(): Promise<void> {
    if (!this.pendingSubmission || this.selectingLeaderboard) {
      return;
    }

    this.selectingLeaderboard = true;
    this.cdr.markForCheck();

    try {
      const accessToken = await this.keycloak.getToken();
      await this.leadershipApiService.selectBenchmarkSubmission(
        this.activeDatasetId,
        this.pendingSubmission.id,
        accessToken,
      );
      this.toasterService.success('Submission added to leaderboard.');
      this.leaderboardConfirmVisible = false;
      this.pendingSubmission = null;
      this.currentLeaderboardSubmission = null;
      await this.loadSubmissions();
    } catch (error) {
      console.error('Error selecting leaderboard submission:', error);
      this.toasterService.error(this.getErrorMessage(error));
    } finally {
      this.selectingLeaderboard = false;
      this.cdr.markForCheck();
    }
  }

  private orderMetricKeys(keys: string[]): string[] {
    const unique = [...new Set(keys)];
    const ordered = PREFERRED_METRIC_ORDER.filter((key) => unique.includes(key));
    const remainder = unique
      .filter((key) => !PREFERRED_METRIC_ORDER.includes(key))
      .sort((a, b) => a.localeCompare(b));
    return [...ordered, ...remainder];
  }

  private formatMetricChange(current: number | null, next: number): string {
    if (current == null) {
      return 'new';
    }
    const delta = next - current;
    if (Math.abs(delta) < 0.00005) {
      return '0.0000';
    }
    const sign = delta > 0 ? '+' : '';
    return `${sign}${delta.toFixed(4)}`;
  }
}
